import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Layout, Layouts } from 'react-grid-layout';
import debounce from 'lodash/debounce';

import { UserDashboard, UserDashboards, WidgetItem } from '@vyce/core/src/types';
import { DeviceContext, TimeDashboardModuleContext, TimeModuleContext } from '@vyce/core/src/contexts';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import {
  filterWidgetsByPermission,
  TIME_WIDGET_DEFAULT_PERMISSIONS,
  TIME_WIDGET_PERMISSIONS,
} from '../widgetPermissions';

export const useTimeDashboardData = () => {
  const [timeDashboard, setTimeDashboard] = useState<UserDashboard>();
  const { isMobile } = useContext(DeviceContext);
  const { dashboards, name, selectedCompanyId, updateUserDashboards, chatUser } =
    useContext(TimeDashboardModuleContext);
  const { locations } = useContext(TimeModuleContext);
  const { canAnywhere, ready } = usePermissions();
  // Widgets whose own request came back 403 — the backstop for a wrong entry in
  // the permission table. Session-only and per company: switching company means
  // different rights, and a right granted later must bring the widget back.
  const [forbiddenWidgets, setForbiddenWidgets] = useState<string[]>([]);

  const hideWidget = useCallback((systemName: string) => {
    setForbiddenWidgets(current => (current.includes(systemName) ? current : [...current, systemName]));
  }, []);

  useEffect(() => {
    setForbiddenWidgets([]);
  }, [selectedCompanyId]);

  /**
   * What the dashboard renders. `timeDashboard` itself stays whole: it is what
   * gets PUT back to /dashboards on every layout change, so filtering it would
   * delete the hidden widgets from the user's record for good.
   */
  const visibleWidgets = useMemo(
    () =>
      filterWidgetsByPermission(
        timeDashboard?.widgets || [],
        TIME_WIDGET_PERMISSIONS,
        TIME_WIDGET_DEFAULT_PERMISSIONS,
        canAnywhere
      ).filter(widget => !forbiddenWidgets.includes(widget.system_name)),
    [timeDashboard?.widgets, canAnywhere, forbiddenWidgets]
  );

  /**
   * Every widget filtered out, rather than "still loading". Both the dashboard
   * record and the rights have to be in before an empty list means anything —
   * otherwise the screen would flash "no access" on the way to being ready.
   */
  const noWidgetsAvailable = ready && !!timeDashboard?.widgets?.length && !visibleWidgets.length;

  const onWidgetStatusChange = useCallback(
    (systemName: string) => {
      if (timeDashboard?.widgets?.length === 0) return;
      setTimeDashboard(value => {
        const newWidgets: WidgetItem[] =
          value?.widgets?.map(item => {
            return item.system_name === systemName ? { ...item, is_active: !item.is_active } : item;
          }) || [];
        return { ...value, widgets: newWidgets };
      });
    },
    [timeDashboard?.widgets]
  );

  const updateDashboards = useCallback(
    (newWidgets: UserDashboards) => {
      updateUserDashboards(newWidgets);
    },
    [updateUserDashboards]
  );

  const handleUpdateDashboards = useMemo(() => debounce(updateDashboards, 500), [updateDashboards]);

  const resetDashboards = () => {
    if (timeDashboard && timeDashboard.widgets) {
      const restoredDashboard = timeDashboard.widgets.map(item => {
        return { ...item, is_active: true };
      });
      setTimeDashboard(value => ({ ...value, widgets: restoredDashboard }));
      updateUserDashboards({
        ...dashboards,
        time: { ...timeDashboard, widgets: restoredDashboard },
      });
    }
  };

  const onLayoutChange = (currentLayout: Layout[], newLayouts: Layouts) => {
    // react-grid-layout fires this on mount as well. With nothing rendered yet —
    // rights not resolved, or every widget filtered out — the layouts it reports
    // are empty, and saving them would wipe the user's own arrangement.
    if (!visibleWidgets.length) {
      return;
    }
    const newWidgetsSettings = { ...timeDashboard, layouts: newLayouts };
    setTimeDashboard(newWidgetsSettings);
    handleUpdateDashboards({
      ...dashboards,
      time: newWidgetsSettings,
    });
  };

  // The dialog only ever sees the visible widgets, so merge its answer by
  // system_name instead of replacing the list — a plain replace would drop the
  // hidden ones out of state and then out of the next PUT.
  const onWidgetsChangeViaSettings = (newWidget: WidgetItem[]) => {
    const changedBySystemName = new Map(newWidget.map(item => [item.system_name, item]));
    setTimeDashboard(value => ({
      ...value,
      widgets: (value?.widgets || []).map(item => changedBySystemName.get(item.system_name) ?? item),
    }));
  };

  useEffect(() => {
    if (dashboards?.time) {
      setTimeDashboard(dashboards.time);
    }
  }, [dashboards?.time]);

  return {
    isMobile,
    name,
    timeDashboard,
    visibleWidgets,
    noWidgetsAvailable,
    hideWidget,
    selectedCompanyId,
    locations,
    onWidgetsChangeViaSettings,
    onWidgetStatusChange,
    onLayoutChange,
    resetDashboards,
    chatUser,
  };
};
